<script setup>
/**
 * A stored answer set (listing_facts) as people read it: question, the answer
 * chosen, the note typed with it, and - not compact - the phrase the listing
 * is built from. Works from the stored JSON alone; no script lookup.
 */
import {computed} from 'vue'

const props = defineProps({
  facts: {type: Object, default: null},   // {script, version, answered, by, answers:[...]}
  compact: {type: Boolean, default: false},
})

const rows = computed(() => (props.facts && props.facts.answers) || [])
const answered = computed(() => rows.value.filter(r => !r.skipped))

// a bullet-list question ("What comes with it?") keeps its answer as items, one line each
const itemsOf = r => (Array.isArray(r.items) ? r.items : []).filter(i => String(i || '').trim() !== '')
</script>

<template>
  <div v-if="!facts || !rows.length" class="text-muted" :class="compact ? 'small' : ''">
    {{ facts ? 'Every question skipped' : 'Not answered' }}
  </div>
  <div v-else-if="compact" class="small">
    <div class="text-muted" v-if="facts.answered">{{ facts.answered | timediff }}<span v-if="facts.by_name"> by {{ facts.by_name }}</span></div>
    <div v-for="r in answered" :key="r.q">
      <div class="text-truncate">
        <span class="text-muted">{{ r.prompt }}</span>
        {{ r.label || r.free_text }}<span v-if="r.label && r.free_text" class="text-muted"> - {{ r.free_text }}</span>
        <span v-if="!r.label && !r.free_text && !itemsOf(r).length" class="text-muted font-italic">none</span>
      </div>
      <ul v-if="itemsOf(r).length" class="facts-items mb-0">
        <li v-for="(i, n) in itemsOf(r)" :key="n">{{ i }}</li>
      </ul>
    </div>
    <div v-if="!answered.length" class="text-muted">Every question skipped</div>
  </div>
  <dl v-else class="mb-0 facts">
    <template v-for="r in rows">
      <dt :key="r.q + '-q'" class="small text-muted font-weight-normal">{{ r.prompt }}</dt>
      <dd :key="r.q + '-a'" :class="{'text-muted font-italic': r.skipped}">
        <template v-if="r.skipped">skipped</template>
        <template v-else>
          {{ r.label || r.free_text }}
          <div v-if="r.label && r.free_text" class="small">{{ r.free_text }}</div>
          <ul v-if="itemsOf(r).length" class="facts-items mb-0">
            <li v-for="(i, n) in itemsOf(r)" :key="n">{{ i }}</li>
          </ul>
          <span v-else-if="!r.label && !r.free_text" class="text-muted font-italic">none</span>
          <div v-if="r.phrase" class="small text-muted">“{{ r.phrase }}”</div>
        </template>
      </dd>
    </template>
  </dl>
</template>

<style scoped>
.facts dd { margin-bottom: .4rem; }
.facts-items { padding-left: 1.25rem; }
</style>
